import { useState } from 'react'
import { useGetAdminAiUsageQuery, useGetAdminAgentsQuery, useGetAdminUserAiUsageQuery } from '../../store/api/agentsApi'
import { COLOR_WARNING } from '../../lib/config'
import Avatar from '../../components/ui/Avatar'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import { StatCards, DateRangePicker, UserSearchDropdown, AgentSearchDropdown, toYMD } from '../../components/ui'
import type { UserSearchOption } from '../../components/ui'
import s from '../../styles/pages/AdminPage.module.css'

// ── Helpers ───────────────────────────────────────────────────────────────────

const SORT_OPTS = [
  { key: 'tokens',   label: 'Tokens'   },
  { key: 'cost',     label: 'Cost'     },
  { key: 'requests', label: 'Requests' },
] as const

const fmt     = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
const fmtCost = (n: number) => n === 0 ? '$0.000' : `$${n.toFixed(4)}`

// ── ExpandedModelBreakdown ────────────────────────────────────────────────────

function ExpandedModelBreakdown({ userId, from, to, agentId }: { userId: string; from: string; to: string; agentId?: string }) {
  const { data, isFetching } = useGetAdminUserAiUsageQuery({ userId, from, to, agentId })
  const byModel = data?.byModel ?? []
  const maxTokens = byModel[0]?.totalTokens ?? 1
  const REQUEST_TYPE_LABELS: Record<string, string> = { discussion: '🗨 Discussion', chat: '💬 Chat' }

  if (isFetching) return (
    <div style={{ padding: '0.75rem 1rem 0.75rem 3.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>Loading…</div>
  )
  if (byModel.length === 0) return (
    <div style={{ padding: '0.75rem 1rem 0.75rem 3.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>No model breakdown available.</div>
  )

  return (
    <div style={{ background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)' }}>
      {byModel.map((row, i) => (
        <div key={`${row.model}-${row.requestType}`}
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.55rem 1rem 0.55rem 3.5rem', borderBottom: i < byModel.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px', minWidth: 0 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>{row.model}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
              {row.provider} · {REQUEST_TYPE_LABELS[row.requestType] ?? row.requestType}
            </div>
          </div>
          <div style={{ flex: '2 1 160px', minWidth: '120px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{fmt(row.totalTokens)} tokens</span>
              <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)' }}>{row.requestCount} req{row.requestCount !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ height: 3, background: 'var(--bg-card)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: 'var(--accent-primary)', opacity: 0.7, borderRadius: 2, width: `${Math.min(100, (row.totalTokens / maxTokens) * 100)}%` }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
              <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)' }}>in: {fmt(row.promptTokens)}</span>
              <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)' }}>out: {fmt(row.completionTokens)}</span>
            </div>
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: row.costUsd > 0.1 ? COLOR_WARNING : 'var(--text-secondary)', flexShrink: 0, minWidth: '4.5rem', textAlign: 'right' }}>
            {fmtCost(row.costUsd)}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.4rem 1rem', borderTop: '1px solid var(--border)', gap: '1.5rem' }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Total: {fmt(byModel.reduce((a, r) => a + r.totalTokens, 0))} tokens</span>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)' }}>{fmtCost(byModel.reduce((a, r) => a + r.costUsd, 0))}</span>
      </div>
    </div>
  )
}

// ── AdminAiUsage ──────────────────────────────────────────────────────────────

export function AdminAiUsage() {
  const { data: agentsData } = useGetAdminAgentsQuery()
  const agents = agentsData?.agents ?? []

  const [from, setFrom]               = useState(toYMD(new Date()))
  const [to,   setTo]                 = useState(toYMD(new Date()))
  const [agentId, setAgentId]         = useState('')
  const [selectedUser, setSelectedUser] = useState<UserSearchOption | null>(null)
  const [sortBy, setSortBy]           = useState<'tokens' | 'cost' | 'requests'>('tokens')
  const [offset, setOffset]           = useState(0)
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)

  const resetOffset = () => { setOffset(0); setExpandedUserId(null) }

  const handleCustomDate = (field: 'from' | 'to', val: string) => {
    if (field === 'from') setFrom(val)
    else setTo(val)
    resetOffset()
  }

  const { data, isFetching, refetch } = useGetAdminAiUsageQuery({
    from, to,
    agentId:  agentId  || undefined,
    userId:   selectedUser?.id,
    sortBy,
    offset,
  })

  const rows      = data?.data    ?? []
  const totals    = data?.totals
  const maxTokens = rows[0]?.totalTokens ?? 1

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>🧑‍💻 AI Usage by User — Ekkam Token</div>
        <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <DateRangePicker
          from={from} to={to}
          onFromChange={val => handleCustomDate('from', val)}
          onToChange={val => handleCustomDate('to', val)}
        />

        <UserSearchDropdown value={selectedUser} onChange={u => { setSelectedUser(u); resetOffset() }} />

        <AgentSearchDropdown
          agents={agents}
          value={agentId}
          onChange={id => { setAgentId(id); resetOffset() }}
        />

        <div style={{ display: 'flex', gap: '0.25rem', background: 'var(--bg-elevated)', padding: '0.2rem', borderRadius: '8px', border: '1px solid var(--border)' }}>
          {SORT_OPTS.map(o => (
            <button key={o.key} onClick={() => { setSortBy(o.key); resetOffset() }}
              style={{ padding: '0.25rem 0.65rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: sortBy === o.key ? 'var(--accent-primary)' : 'transparent', color: sortBy === o.key ? 'white' : 'var(--text-muted)', transition: 'all 0.12s' }}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      {totals && (
        <StatCards
          style={{ marginBottom: '1.25rem' }}
          stats={[
            { icon: '🔢', label: 'Total Requests', value: fmt(totals.requestCount) },
            { icon: '📝', label: 'Prompt Tokens',  value: fmt(totals.promptTokens) },
            { icon: '💬', label: 'Output Tokens',  value: fmt(totals.completionTokens) },
            { icon: '💰', label: 'Est. Cost',       value: fmtCost(totals.costUsd) },
          ]}
        />
      )}

      {/* User rows */}
      <div className={s.listCard}>
        {isFetching && rows.length === 0 && <div className={s.listCardLoading}>Loading…</div>}
        {!isFetching && rows.length === 0 && <div className={s.listCardEmpty}>No usage data for this period.</div>}
        {rows.map((row, i) => {
          const isExpanded = expandedUserId === row.userId
          return (
            <div key={row.userId} style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
              {/* Summary row — click to expand */}
              <div onClick={() => setExpandedUserId(isExpanded ? null : row.userId)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', cursor: 'pointer', background: isExpanded ? 'rgba(124,58,237,0.06)' : 'transparent', transition: 'background 0.12s' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', width: '1.5rem', textAlign: 'right', flexShrink: 0 }}>{offset + i + 1}</span>
                <Avatar name={row.displayName} avatarUrl={row.avatarUrl} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.displayName}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.email}</div>
                </div>
                <div className={s.barCol}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{fmt(row.totalTokens)} tokens</span>
                    <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{row.requestCount} reqs</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 2, width: `${Math.min(100, (row.totalTokens / maxTokens) * 100)}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: row.costUsd > 0.1 ? COLOR_WARNING : 'var(--text-secondary)', flexShrink: 0, width: '4.5rem', textAlign: 'right' }}>{fmtCost(row.costUsd)}</span>
                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', flexShrink: 0, width: '1rem', textAlign: 'center' }}>{isExpanded ? '▴' : '▾'}</span>
              </div>
              {/* Expanded model breakdown */}
              {isExpanded && (
                <ExpandedModelBreakdown userId={row.userId} from={from} to={to} agentId={agentId || undefined} />
              )}
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {(offset > 0 || data?.hasMore) && (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          {offset > 0 && <button onClick={() => setOffset(Math.max(0, offset - 20))} style={{ padding: '0.35rem 0.85rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 7, fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>← Prev</button>}
          {data?.hasMore && <button onClick={() => setOffset(data.nextOffset ?? offset + 20)} style={{ padding: '0.35rem 0.85rem', background: 'var(--accent-primary)', border: 'none', borderRadius: 7, fontSize: '0.78rem', color: 'white', fontWeight: 600, cursor: 'pointer' }}>Next →</button>}
        </div>
      )}
    </div>
  )
}
